import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { financeiroAPI, clientsAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, FileText, Search, Filter, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate, useSearchParams } from 'react-router-dom';
import ServiceFormModal from './ServiceFormModal';
import ServiceListRow from './ServiceListRow';
import { totalManejo } from './ManejoGrid';
import { imprimirRecibo } from './recibo';

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

// Ordenação da lista. Padrão: ordem de criação, mais recentes primeiro.
// Clicar num título de coluna ordena por ela; clicar de novo inverte; o 3º clique volta para a ordem de criação.
const ORDEM_PADRAO = { col: 'criacao', dir: 'desc' };
const txt = (v) => (v || '').toString().toLowerCase();
const VALOR_COLUNA = {
  criacao: (s) => s.created_at || s.data_inicio || '',
  cliente: (s) => txt(s.cliente_nome),
  servico: (s) => txt(s.titulo),
  responsavel: (s) => txt(s.criado_por_nome),
  valor: (s) => Number(s.valor_total) || 0,
  inicio: (s) => s.data_inicio || '',
  manejo: (s) => (s.gerir_manejo ? (s.manejo_total ?? totalManejo(s.manejo)) : -1),
};
const DIR_INICIAL = { cliente: 'asc', servico: 'asc', responsavel: 'asc', valor: 'desc', inicio: 'desc', manejo: 'desc' };
const comparar = (a, b) => (typeof a === 'number' && typeof b === 'number'
  ? a - b
  : String(a).localeCompare(String(b), 'pt-BR'));

const emptyFilters = {
  status: '',
  search: '',
  responsavel_id: '',
  mes: '',
  data_inicio: '',
  data_fim: '',
  com_manejo: '',  // '' | 'sim' | 'nao'
};

export default function FinanceiroServicos() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const [searchParams] = useSearchParams();
  const focusSvcId = searchParams.get('svc') || null;
  const focusRef = useRef(null);
  const [services, setServices] = useState([]);
  const [types, setTypes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [ordem, setOrdem] = useState(ORDEM_PADRAO);

  const clicarColuna = (col) => setOrdem(o => {
    if (o.col !== col) return { col, dir: DIR_INICIAL[col] || 'asc' };
    if (o.dir === DIR_INICIAL[col]) return { col, dir: o.dir === 'asc' ? 'desc' : 'asc' };
    return ORDEM_PADRAO;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Se há focusSvcId, garante incluir TODOS os status (mesmo finalizados)
      const svcParams = focusSvcId ? {} : { em_andamento: true };
      if (filters.status) {
        delete svcParams.em_andamento;
        svcParams.status = filters.status;
      }
      if (filters.responsavel_id) svcParams.responsavel_id = filters.responsavel_id;
      if (filters.mes) svcParams.mes = filters.mes;
      if (filters.data_inicio) svcParams.data_inicio = filters.data_inicio;
      if (filters.data_fim) svcParams.data_fim = filters.data_fim;
      if (filters.com_manejo === 'sim') svcParams.com_manejo = true;

      const [svc, tp, pm, cat, cli, resp] = await Promise.all([
        financeiroAPI.listServices(svcParams),
        financeiroAPI.listServiceTypes(),
        financeiroAPI.listPaymentMethods(),
        financeiroAPI.listCategories(),
        clientsAPI.list(),
        financeiroAPI.listResponsaveis(),
      ]);
      setServices(svc.data || []);
      setTypes(tp.data || []);
      setMethods(pm.data || []);
      setCategories(cat.data || []);
      setClients(cli.data || []);
      setResponsaveis(resp.data || []);
    } catch (e) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.responsavel_id, filters.mes, filters.data_inicio, filters.data_fim, filters.com_manejo, focusSvcId]);

  useEffect(() => { if (financeiroAtivo) load(); }, [financeiroAtivo, load]);

  useEffect(() => {
    if (!loading && focusSvcId && focusRef.current) {
      setTimeout(() => {
        focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [loading, focusSvcId, services]);

  // Aplica busca textual + filtro "sem manejo" client-side + ordenação escolhida
  const filtered = useMemo(() => {
    let list = services.slice();
    if (filters.com_manejo === 'nao') {
      list = list.filter(s => !s.gerir_manejo);
    }
    const q = (filters.search || '').toLowerCase();
    if (q) {
      const qDigits = onlyDigits(q);
      list = list.filter(s =>
        (s.titulo || '').toLowerCase().includes(q) ||
        (s.cliente_nome || '').toLowerCase().includes(q) ||
        (qDigits && onlyDigits(s.cliente_cpf || '').includes(qDigits))
      );
    }
    const valor = VALOR_COLUNA[ordem.col] || VALOR_COLUNA.criacao;
    const sinal = ordem.dir === 'asc' ? 1 : -1;
    list.sort((a, b) =>
      sinal * comparar(valor(a), valor(b))
      // desempate: mais recente primeiro
      || comparar(VALOR_COLUNA.criacao(b), VALOR_COLUNA.criacao(a)));
    return list;
  }, [services, filters.search, filters.com_manejo, ordem]);

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este serviço? Esta ação não pode ser desfeita.')) return;
    try {
      await financeiroAPI.deleteService(id);
      toast.success('Serviço excluído');
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const clearAllFilters = () => setFilters(emptyFilters);

  const filtrosAtivos =
    !!filters.status || !!filters.responsavel_id || !!filters.mes ||
    !!filters.data_inicio || !!filters.data_fim || !!filters.com_manejo;

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return <Navigate to="/" replace />;

  return (
    <div className="space-y-5" data-testid="financeiro-servicos">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Serviços</h1>
          <p className="text-sm text-muted-foreground">
            {ordem.col === 'criacao'
              ? `Ordem de criação (${ordem.dir === 'desc' ? 'mais recentes primeiro' : 'mais antigos primeiro'})`
              : 'Ordenado pela coluna marcada com a seta'}
            {' · '}
            {ordem.col === 'criacao' ? (
              <button type="button" className="underline hover:text-foreground"
                onClick={() => setOrdem(o => ({ col: 'criacao', dir: o.dir === 'desc' ? 'asc' : 'desc' }))}
                data-testid="btn-inverter-criacao">inverter</button>
            ) : (
              <button type="button" className="underline hover:text-foreground"
                onClick={() => setOrdem(ORDEM_PADRAO)} data-testid="btn-ordem-criacao">voltar à ordem de criação</button>
            )}
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="btn-novo-servico">
          <Plus className="w-4 h-4 mr-2" /> Novo Serviço
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="relative md:col-span-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título, cliente ou CPF..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                data-testid="filter-search"
              />
            </div>
            <div className="md:col-span-3">
              <Select value={filters.status || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'todos' ? '' : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os ativos</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="aguardando_cliente">Aguardando Cliente</SelectItem>
                  <SelectItem value="aguardando_pagamento">Aguardando Pagamento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex items-center justify-end gap-2">
              <Button
                variant={showAdvanced ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowAdvanced(s => !s)}
                data-testid="btn-toggle-filtros"
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" /> Filtros{filtrosAtivos ? ' •' : ''}
              </Button>
              <span className="text-sm text-muted-foreground whitespace-nowrap">{filtered.length} serviço(s)</span>
            </div>
          </div>

          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 pt-3 border-t" data-testid="filtros-avancados">
              <div>
                <Label className="text-xs">Responsável</Label>
                <Select value={filters.responsavel_id || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, responsavel_id: v === 'todos' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-responsavel"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {responsaveis.map(r => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Mês (YYYY-MM)</Label>
                <Input type="month" value={filters.mes} onChange={(e) => setFilters(f => ({ ...f, mes: e.target.value }))} data-testid="filter-mes" />
              </div>
              <div>
                <Label className="text-xs">De</Label>
                <Input type="date" value={filters.data_inicio} onChange={(e) => setFilters(f => ({ ...f, data_inicio: e.target.value, mes: '' }))} data-testid="filter-data-inicio" />
              </div>
              <div>
                <Label className="text-xs">Até</Label>
                <Input type="date" value={filters.data_fim} onChange={(e) => setFilters(f => ({ ...f, data_fim: e.target.value, mes: '' }))} data-testid="filter-data-fim" />
              </div>
              <div>
                <Label className="text-xs">Manejo</Label>
                <Select value={filters.com_manejo || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, com_manejo: v === 'todos' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-manejo"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="sim">Com Manejo</SelectItem>
                    <SelectItem value="nao">Sem Manejo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filtrosAtivos && (
                <div className="md:col-span-5 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} data-testid="btn-limpar-filtros">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Limpar filtros
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cabeçalho da lista — clique no título para ordenar pela coluna */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase text-muted-foreground font-semibold tracking-wider border-b">
        <div className="col-span-1"></div>
        <CabecalhoOrdem col="cliente" ordem={ordem} onClick={clicarColuna} className="col-span-3">Cliente</CabecalhoOrdem>
        <CabecalhoOrdem col="servico" ordem={ordem} onClick={clicarColuna} className="col-span-3">Serviço</CabecalhoOrdem>
        <CabecalhoOrdem col="responsavel" ordem={ordem} onClick={clicarColuna} className="col-span-2">Responsável</CabecalhoOrdem>
        <CabecalhoOrdem col="valor" ordem={ordem} onClick={clicarColuna} className="col-span-1 justify-end">Valor</CabecalhoOrdem>
        <CabecalhoOrdem col="inicio" ordem={ordem} onClick={clicarColuna} className="col-span-1">Início</CabecalhoOrdem>
        <CabecalhoOrdem col="manejo" ordem={ordem} onClick={clicarColuna} className="col-span-1 justify-center">Manejo</CabecalhoOrdem>
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando serviços...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Nenhum serviço encontrado para os filtros atuais.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(svc => {
            const isFocus = focusSvcId === svc.id;
            return (
              <div
                key={svc.id}
                ref={isFocus ? focusRef : null}
                className={isFocus ? 'ring-2 ring-cyan-400 ring-offset-2 rounded-lg' : ''}
              >
                <ServiceListRow
                  svc={svc}
                  onChanged={load}
                  onEdit={(s) => { setEditing(s); setShowForm(true); }}
                  onDelete={handleDelete}
                  onPrintRecibo={imprimirRecibo}
                  defaultExpanded={isFocus}
                />
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ServiceFormModal
          editing={editing}
          types={types}
          methods={methods}
          categories={categories}
          clients={clients}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function CabecalhoOrdem({ col, ordem, onClick, className = '', children }) {
  const ativo = ordem.col === col;
  const Icone = !ativo ? ArrowUpDown : ordem.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onClick(col)}
      className={`flex items-center gap-1 uppercase tracking-wider font-semibold hover:text-foreground transition-colors ${ativo ? 'text-foreground' : ''} ${className}`}
      title={ativo ? (ordem.dir === 'asc' ? 'Crescente — clique para inverter' : 'Decrescente — clique para inverter') : 'Ordenar por esta coluna'}
      data-testid={`ordenar-${col}`}
    >
      {children}
      <Icone className={`w-3 h-3 shrink-0 ${ativo ? '' : 'opacity-40'}`} />
    </button>
  );
}
